import {
  Box,
  Stack,
  Typography,
  alpha,
  darken,
  hexToRgb,
  styled,
  useTheme,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { ErrorCode, FileError, FileRejection } from "react-dropzone";

import { FileInfo } from "./census-dropzone-accepted-file";
import { MAX_SIZE } from "./constants";
import { formatBytes } from "./utils";

const ErrorBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  backgroundColor: alpha(hexToRgb(theme.palette.dropzone.error), 0.1),
  cursor: "pointer",
}));

interface CensusDropzoneErrorFileProps {
  rejectedFile: FileRejection;
}

interface IErrorData {
  title: string;
  description: React.ReactNode;
}

const CensusDropzoneErrorFile = ({
  rejectedFile,
}: CensusDropzoneErrorFileProps) => {
  const theme = useTheme();

  const darkerColor = darken(theme.palette.dropzone.error, 0.6);

  const getErrorData = (error: FileError, fileName?: string) => {
    if (error.code === ErrorCode.FileTooLarge) {
      return {
        title: "File size exceeded",
        description: (
          <Typography variant="small" color={darkerColor} component="p">
            <Typography
              variant="smallBold"
              component="span"
              color={darkerColor}
            >
              {fileName}
            </Typography>{" "}
            could not be uploaded. File size exceeds the maximum (
            {formatBytes(MAX_SIZE)})
          </Typography>
        ),
      };
    } else if (error.code === ErrorCode.FileInvalidType) {
      return {
        title: "File extension is not supported",
        description: (
          <Typography variant="small" color={darkerColor} component="p">
            Please make sure you upload a valid
            <Typography
              variant="smallBold"
              component="span"
              color={darkerColor}
            >
              {" "}
              .xls
            </Typography>{" "}
            or{" "}
            <Typography
              variant="smallBold"
              component="span"
              color={darkerColor}
            >
              .xlsx
            </Typography>{" "}
            file.
          </Typography>
        ),
      };
    } else if (error.code === ErrorCode.TooManyFiles) {
      return {
        title: "Uploading multiple files is not supported.",
        description: (
          <Typography variant="small" color={darkerColor} component="p">
            Please upload only one file.
          </Typography>
        ),
      };
    }
    return null;
  };

  const errorData: IErrorData | null = getErrorData(
    rejectedFile.errors[0],
    rejectedFile.file.name,
  );

  if (!errorData) {
    return null;
  }

  return (
    <ErrorBox mt={3}>
      <ErrorOutlineIcon sx={{ color: theme.palette.dropzone.error }} />
      <FileInfo>
        <Typography variant="body" color={darkerColor} fontWeight={600}>
          {errorData?.title}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {errorData?.description}
        </Stack>
      </FileInfo>
    </ErrorBox>
  );
};

export default CensusDropzoneErrorFile;
